import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import NumberFormat from "../common/NumberFormat";
import ReactTooltip from "react-tooltip";
import UnitStatus from "../common/UnitStatus";
import UnitMode from "../common/UnitMode";
import PopupWrapper from "../common/PopupWrapper";
import AutoPilotConfigEditButton from "./AutoPilotConfigEditButton";
import DateTimeFormatter from "../common/DateTimeFormatter";
import ClickToCopyWrapper from "../common/ClickToCopyWrapper";
import UnitRevenueLiftTrendModal from "./UnitRevenueLiftTrendModal";
import UnitMetricTrendModal from "./UnitMetricTrendModal";
import DemandTypePortfolioButton from "components/common/DemandTypePortfolioButton";
import AppliedUprModal from "components/anatomy-cst/AppliedUprModal";
import PublisherFirstLookModal from "components/anatomy-cst/PublisherFirstLookModal";

import { NET_REV, netRevTitle } from "constants/Unit";
import UnitHasParentButton from "./UnitHasParentButton";
import UnitHasChildrenButton from "./UnitHasChildrenButton";
import Avg7DaySplotline from "./Avg7DaySplotline";
import SimpleTooltip from "components/common/SimpleTooltip";

import { FaCaretDown } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { IoIosPause } from "react-icons/io";

// show original if no abbreviation is provided
const demandChannelsMap = {
  "Ad Exchange": "Adx",
  AdSense: "Ads",
  "Exchange Bidding": "EB",
  "Price Priority": "PP",
  "Header Bidding": "HB",
  // SMART: "SMART",
  Sponsorship: "Sponsor",
  "Billable House": "BH",
  "Preferred Deal": "PD",
  Mediation: "Mediation",
  "SDK Bidding": "SDK Bidding",
};

const demandTypePriorityMap = {
  Sponsor: 1,
  Standard: 2,
  PD: 3,
  Adx: 4,
  Ads: 5,
  OB: 6,
  EB: 7,
  "Billable House": 8,
  PP: 9,
  Bulk: 10,
  House: 12,
  Network: 11,
  Mediation: 13,
  "SDK Bidding": 14,
};

class AdUnitBasicCard extends React.Component {
  constructor(props) {
    super(props);

    this.handleGoToCstAnatomy = this.handleGoToCstAnatomy.bind(this);
    this.handleGoToObserver = this.handleGoToObserver.bind(this);
    this.handleGoToUnitInsight = this.handleGoToUnitInsight.bind(this);
    this.handleGoToUnitAPConfigPage =
      this.handleGoToUnitAPConfigPage.bind(this);
  }

  handleGoToCstAnatomy(unitId) {
    window.open(`${window.location.origin}/#/anatomy/cst/${unitId}`);
  }

  handleGoToObserver(unitId) {
    window.open(`${window.location.origin}/#/anatomy/${unitId}`);
  }

  handleGoToUnitInsight(unitId) {
    window.open(`${window.location.origin}/#/unit-insight/${unitId}`);
  }

  handleGoToUnitAPConfigPage(unitId) {
    window.open(
      `${window.location.origin}/#/control-center/unit/${unitId}/auto-pilot/config`
    );
  }

  render() {
    const {
      unit,
      networkName,
      networkId,
      type,
      showFullWidthCard,
      isCompactMode,
      isBasicInfoOnly, // for unit with no snapshot info
      isInTwd = false,
    } = this.props;

    const demandTypes = _(unit.demand_types ? unit.demand_types.split(",") : [])
      .map((t) => {
        return demandChannelsMap[t] || t;
      })
      .uniq()
      .compact()
      .orderBy((t) => {
        return demandTypePriorityMap[t];
      })
      .value();

    if (type === "NOT_ONBOARDED_UNITS") {
      return (
        <UnitCardNotOnboarded
          unit={unit}
          demandTypes={demandTypes}
          showFullWidthCard={showFullWidthCard}
        ></UnitCardNotOnboarded>
      );
    } else {
      return (
        <UnitCardRevenueDriver
          unit={unit}
          type={type}
          demandTypes={demandTypes}
          networkName={networkName}
          networkId={networkId}
          handleGoToCstAnatomy={this.handleGoToCstAnatomy}
          handleGoToObserver={this.handleGoToObserver}
          handleGoToUnitInsight={this.handleGoToUnitInsight}
          handleGoToUnitAPConfigPage={this.handleGoToUnitAPConfigPage}
          handleOpenModal={this.props.handleOpenModal}
          handleCloseModal={this.props.handleCloseModal}
          showFullWidthCard={showFullWidthCard}
          isCompactMode={isCompactMode}
          isBasicInfoOnly={isBasicInfoOnly}
          isInTwd={isInTwd}
        ></UnitCardRevenueDriver>
      );
    }
  }
}

class UnitMetricPerDay extends React.Component {
  render() {
    let { unit, day, flag = "" } = this.props;
    const weekday = moment(unit.date).format("ddd");

    return (
      <div>
        <div
          className={`mb-1 mt-2 text-center text-xs font-semibold ${
            day === 1
              ? "bg-gray-400 text-gray-800"
              : "bg-gray-200 text-gray-700"
          }`}
        >
          <span
            data-tip
            data-for={`date-${unit.gam_unit_id}-${day}`}
            className={`${
              weekday !== "Sun" && weekday !== "Sat" ? "" : "underline"
            }`}
          >
            {moment(unit.date).format("YYYY-MM-DD ddd")}
          </span>
        </div>
        <div
          className={`flex justify-between text-right ${
            day === 1 ? "font-semibold" : ""
          }`}
        >
          <div className="flex flex-col justify-between text-right text-xs">
            <div className="leading-snug">Rev Ratio</div>

            <div>
              <small>Raw Bill: </small>
              <NumberFormat
                value={unit.billable_rev_ratio * 100}
                postfix="%"
              ></NumberFormat>
            </div>

            <div className="flex items-center gap-1 font-light leading-none">
              <small>
                G:{" "}
                <NumberFormat
                  value={unit.google_rev_ratio * 100}
                  postfix="%"
                ></NumberFormat>
              </small>
              <span className="text-gray-500">·</span>
              <small>
                H:{" "}
                <NumberFormat
                  value={unit.holistic_rev_ratio * 100}
                  postfix="%"
                ></NumberFormat>
              </small>
            </div>
          </div>

          <div className={`flex flex-col justify-between text-right text-xs `}>
            <div className="leading-snug">Net Google</div>
            <div
              className={`${flag === NET_REV.GOOGLE && "bg-blue-200"}
                ${unit.net_google_inc_rev_ratio < 0 ? "text-red-600" : ""}
                `}
            >
              <NumberFormat
                value={unit.net_google_inc_rev_ratio * 100}
                postfix="%"
              ></NumberFormat>
            </div>
            <div
              className={`font-light leading-none ${
                unit.net_google_inc_rev_trans < 0 ? "text-red-600" : ""
              }`}
            >
              {/* <small className="text-gray-800">
                Cost:{" "}
                <NumberFormat
                  value={unit.extra_ad_serving_cost_trans}
                  format="$0,0"
                ></NumberFormat>
              </small>
              <span className="text-gray-500">·</span> */}
              <small
                data-tip
                data-for={`${unit.gam_unit_id}_${unit.date}_cost`}
              >
                <NumberFormat
                  value={unit.net_google_inc_rev_trans}
                  prefix="$"
                ></NumberFormat>
              </small>
              <ReactTooltip
                type="dark"
                effect="solid"
                place="top"
                id={`${unit.gam_unit_id}_${unit.date}_cost`}
              >
                Cost:{" "}
                <NumberFormat
                  value={unit.extra_ad_serving_cost_trans}
                  format="$0,0.00"
                ></NumberFormat>
              </ReactTooltip>
            </div>
          </div>

          <div className={`flex flex-col justify-between text-right text-xs`}>
            <div className="leading-snug">Net Holistic</div>
            <div
              className={`${flag === NET_REV.HOLISTIC && "bg-blue-200"} ${
                unit.net_holistic_inc_rev_ratio < 0 ? "text-red-600" : ""
              }`}
            >
              <NumberFormat
                value={unit.net_holistic_inc_rev_ratio * 100}
                postfix="%"
              ></NumberFormat>
            </div>
            <div
              className={`font-light leading-none ${
                unit.net_holistic_inc_rev_trans < 0 ? "text-red-600" : ""
              }`}
            >
              <small>
                <NumberFormat
                  value={unit.net_holistic_inc_rev_trans}
                  prefix="$"
                ></NumberFormat>
              </small>
            </div>
          </div>

          <div className="flex flex-col justify-between text-right text-xs">
            <div className="leading-snug">Google Rev</div>
            <NumberFormat
              value={unit.google_rev_trans}
              format="$0,0.00a"
            ></NumberFormat>

            <div className="text-xs font-light leading-none">
              <small>
                RPM:{" "}
                <NumberFormat
                  value={unit.google_rev_rpm}
                  format="$0,0.00"
                ></NumberFormat>
              </small>
            </div>
          </div>

          <div className="flex flex-col justify-between text-right text-xs">
            <div className="leading-snug">Holistic Rev</div>
            <div>
              <NumberFormat
                value={unit.holistic_rev_trans}
                format="$0,0.00a"
              ></NumberFormat>
            </div>

            <div className="flex items-center gap-1 font-light leading-none">
              <small className={`${flag === "Request" ? "bg-blue-200" : ""}`}>
                Req:{" "}
                <NumberFormat value={unit.req} format="0,0.00a"></NumberFormat>
              </small>
              <span className="text-gray-500">·</span>
              <small>
                RPM:{" "}
                <NumberFormat
                  value={unit.holistic_rev_rpm}
                  format="$0,0.00"
                ></NumberFormat>
              </small>
            </div>
          </div>

          <div className={`flex flex-col justify-between text-right text-xs`}>
            <div className="leading-snug">Net Bill Lift</div>
            <div
              className={`${flag === NET_REV.BILLABLE && "bg-blue-200"} ${
                unit.net_billable_lift < 0 ? "text-red-600" : ""
              }`}
            >
              <NumberFormat
                value={unit.net_billable_lift * 100}
                postfix="%"
                format="0,0.00"
              ></NumberFormat>
            </div>
            <div className="font-light leading-none">
              <small>
                <NumberFormat
                  value={unit.net_billable_rev_trans}
                  format="$0,0.00"
                ></NumberFormat>
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

class RequestTypesSection extends React.PureComponent {
  render() {
    const { requestTypes } = this.props;
    return (
      <div className="my-2 flex">
        {requestTypes.split(",").map((d) => {
          if (d === "") return "";
          return (
            <div
              key={d}
              className="rounded mr-2 bg-indigo-100 px-2 text-xs text-gray-600"
              title={d}
            >
              {d === "Google Publisher Tag" ? "GPT" : d}
            </div>
          );
        })}
      </div>
    );
  }
}

class InspectionSection extends React.PureComponent {
  render() {
    const { forceInspection } = this.props;
    if (forceInspection) {
      return (
        <div className="border-l px-1 text-xs" title="Force Inspection">
          {forceInspection && (
            <span className="rounded bg-orange-400 px-2 font-bold text-orange-100">
              INSP
            </span>
          )}
        </div>
      );
    } else {
      return "";
    }
  }
}

class BmSection extends React.PureComponent {
  render() {
    const { unitId, bpm, enableAbuse, bmAbuseSizes } = this.props;
    /*
    bmAbuseSizes=[
      [
        "fluid",
        [
          1,
          1
        ]
      ]
    ]
    */
    let abuseSizesString = "None";
    if (bmAbuseSizes) {
      let sizes = bmAbuseSizes[0];
      abuseSizesString = sizes
        .map((s) => {
          if (typeof s === "string") {
            return s;
          } else {
            return s.join("x");
          }
        })
        .join(", ");
    }

    return (
      <div
        className="border-l px-1 text-xs"
        title="Benchmark Performance Multiplier"
      >
        BPM:{" "}
        {bpm < 1 ? (
          <>
            <span
              className="font-bold text-orange-600"
              data-tip
              data-for={`bpm-label-${unitId}`}
            >
              <NumberFormat value={bpm} format="0,0%"></NumberFormat>
            </span>
            <ReactTooltip
              id={`bpm-label-${unitId}`}
              type="dark"
              effect="solid"
              place="top"
            >
              Enable benchmark tfcd abuse:{" "}
              <b>{enableAbuse ? "true" : "false"}</b>
              <br></br>
              Benchmark Abuse Sizes: <b>{abuseSizesString}</b>
            </ReactTooltip>
          </>
        ) : (
          <span className="text-gray-600">
            <NumberFormat value={bpm} format="0,0%"></NumberFormat>
          </span>
        )}
      </div>
    );
  }
}

class SegmentResearchSection extends React.PureComponent {
  render() {
    const { hasSegmentConfig } = this.props;
    return hasSegmentConfig ? (
      <div className="px-1 text-xs" title="Segment Research">
        <span className="rounded bg-purple-500 px-2 font-bold text-pink-100">
          SR
        </span>
      </div>
    ) : (
      ""
    );
  }
}

class CustomSegSection extends React.PureComponent {
  render() {
    const { enabled } = this.props;
    return enabled ? (
      <div className="px-1 text-xs">
        <span className="rounded bg-yellow-600 px-2 font-bold text-yellow-100">
          SEG
        </span>
      </div>
    ) : (
      ""
    );
  }
}

class DebugModeSection extends React.PureComponent {
  render() {
    const { enableDispatcherDebug } = this.props;
    return enableDispatcherDebug ? (
      <div className="px-1 text-xs">
        <span className="rounded bg-pink-500 px-2 font-bold text-pink-100">
          DEBUG
        </span>
      </div>
    ) : (
      ""
    );
  }
}

class MaxTrafficRatioSection extends React.PureComponent {
  render() {
    const { maxTrafficRatio, minTrafficRatio } = this.props;
    return (
      <div className="border-l px-1 text-xs">
        Traffic:{" "}
        <span className="text-gray-600">
          <NumberFormat value={minTrafficRatio} format="0,0%"></NumberFormat>
        </span>
        <span className="px-1">-</span>
        <span className="text-gray-600">
          <NumberFormat value={maxTrafficRatio} format="0,0%"></NumberFormat>
        </span>
      </div>
    );
  }
}

class AdPodsSection extends React.PureComponent {
  render() {
    const { enableAdPods } = this.props;

    return (
      <>
        {enableAdPods ? (
          <div className="border-l px-1 text-xs">
            <span className="font-bold text-purple-600">ADPODS</span>
          </div>
        ) : (
          ""
        )}
      </>
    );
  }
}

class UnitChangeTimeSection extends React.PureComponent {
  render() {
    const { unit, type } = this.props;

    return (
      <>
        <div
          data-tip
          data-for={`unit-changed-at-${unit.gam_unit_id}`}
          className="whitespace-no-wrap border-l px-1 text-xs text-gray-700"
        >
          {type === "NEWLY_ONBOARDED_UNITS" ? (
            <>
              Onboarded{" "}
              <span className="text-gray-900">
                <DateTimeFormatter
                  fromNowOnly={true}
                  datetime={unit.created_at}
                ></DateTimeFormatter>
              </span>
            </>
          ) : (
            <>
              Changes made{" "}
              <span className="text-gray-900">
                <DateTimeFormatter
                  fromNowOnly={true}
                  datetime={unit.last_changed_at}
                ></DateTimeFormatter>
              </span>
            </>
          )}
        </div>

        <ReactTooltip
          id={`unit-changed-at-${unit.gam_unit_id}`}
          type="dark"
          effect="solid"
          place="top"
        >
          <table>
            <tbody>
              <tr>
                <td className="pr-2 text-right">Created At</td>
                <td>
                  <b>
                    <DateTimeFormatter
                      format="YYYY-MM-DD HH:mm Z"
                      datetime={unit.created_at}
                    ></DateTimeFormatter>
                  </b>
                </td>
              </tr>

              <tr>
                <td className="pr-2 text-right">Updated At</td>
                <td>
                  <b>
                    <DateTimeFormatter
                      format="YYYY-MM-DD HH:mm Z"
                      datetime={unit.updated_at}
                    ></DateTimeFormatter>
                  </b>
                </td>
              </tr>

              <tr>
                <td className="pr-2 text-right">
                  Auto Pilot Config Updated At
                </td>
                <td>
                  <b>
                    <DateTimeFormatter
                      format="YYYY-MM-DD HH:mm Z"
                      datetime={unit.config_updated_at}
                    ></DateTimeFormatter>
                  </b>
                </td>
              </tr>
            </tbody>
          </table>
        </ReactTooltip>
      </>
    );
  }
}

class UnitTagsSection extends React.PureComponent {
  render() {
    const { tags, suspendedRatio } = this.props;
    const inventoryTags = _.remove(_.cloneDeep(tags), (t) => {
      return _.startsWith(t, "IVF:");
    });

    return (
      <div className="border-t border-b">
        <div
          style={{ height: "24px" }}
          className="flex items-center justify-between"
        >
          {inventoryTags && !_.isEmpty(inventoryTags) ? (
            <div className="whitespace-no-wrap my-1 flex w-full overflow-x-auto">
              {inventoryTags.map((t) => {
                return (
                  <div
                    key={t}
                    className="rounded mr-2 bg-orange-200 px-2 text-gray-600"
                    style={{ fontSize: "12px" }}
                  >
                    {t}
                  </div>
                );
              })}
            </div>
          ) : (
            <div></div>
          )}
          <div>
            {!_.isNil(suspendedRatio) ? (
              <span className="rounded bg-orange-200 px-2 text-orange-800">
                APSUS:
                <NumberFormat value={suspendedRatio} format="0,0" postfix="%" />
              </span>
            ) : (
              ""
            )}
          </div>
        </div>

        <div className="border-t">
          <div className="whitespace-no-wrap my-1 flex w-full overflow-x-auto">
            {tags &&
              tags.map((t) => {
                return (
                  <div
                    key={t}
                    className="rounded mr-2 bg-orange-200 px-2 text-gray-600"
                    style={{ fontSize: "12px" }}
                  >
                    {t}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  }
}

class DemandTypesSection extends React.PureComponent {
  render() {
    const { demandTypes } = this.props;

    return (
      <>
        {demandTypes.map((d) => {
          return (
            <div
              key={d}
              className="rounded mr-2 bg-gray-200 px-2 text-xs text-gray-600"
            >
              {d}
            </div>
          );
        })}
      </>
    );
  }
}

const MODAL = {
  MONTH_DATA: "MONTH_DATA",
  MONTH_METRIC: "MONTH_METRIC",
  APPLIED_UPR: "APPLIED_UPR",
  APPLIED_FL: "APPLIED_FL",
};

const APPLIED_RULES_DATE_RANGE_DAYS = 7;

class UnitCardRevenueDriver extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      hoverFlag: "",
      dataType: "",
      metricType: "",
      data: null,

      selectedModal: null,
    };

    this.popupRef = React.createRef();

    this.handleOpenMetricModal = this.handleOpenMetricModal.bind(this);
    this.handleOpenRevLiftModal = this.handleOpenRevLiftModal.bind(this);
  }

  handleOpenMetricModal(metricType) {
    if (this.props.handleOpenModal) {
      this.props.handleOpenModal();
    }

    this.setState({
      selectedModal: MODAL.MONTH_METRIC,
      metricType,
    });
  }

  handleOpenRevLiftModal(dataType) {
    if (this.props.handleOpenModal) {
      this.props.handleOpenModal();
    }

    this.setState({
      selectedModal: MODAL.MONTH_DATA,
      dataType,
    });
  }

  render() {
    const {
      unit,
      type,
      demandTypes,
      networkName,
      networkId,
      handleGoToObserver,
      handleGoToCstAnatomy,
      handleGoToUnitInsight,
      handleGoToUnitAPConfigPage,
      showFullWidthCard,
      isCompactMode,
      isBasicInfoOnly,
      isInTwd = false,
    } = this.props;

    const { hoverFlag, selectedModal } = this.state;

    const unitD1 = unit.last_day_metrics;
    const unitD2 = unit.last_2_day_metrics;
    const unitD3 = unit.last_3_day_metrics;

    const unitMetricDaysData = isCompactMode
      ? [unit, unitD1]
      : [unit, unitD1, unitD2, unitD3];

    const autopilot_brief = unit.autopilot_brief || {};
    const isPaused = unit.status === 1 || unit.status === "PAUSED";

    return (
      <div>
        <div
          key={unit.id}
          className="border border-gray-300 bg-white p-2 text-sm text-gray-800"
          style={{ width: showFullWidthCard ? "100%" : "38.5rem" }}
        >
          <div
            className={`relative -ml-2 -mr-2 -mt-2 flex items-baseline justify-between px-2 py-1 text-gray-700 ${
              isPaused ? "bg-orange-100" : "bg-gray-100"
            }`}
          >
            {unit.rank && (
              <div
                className={`rounded-br absolute left-0 top-0 w-8  px-1 py-0 text-center text-xs font-semibold ${
                  unit.is_hero_unit
                    ? "bg-teal-500 text-white"
                    : "bg-teal-200 text-gray-600"
                }`}
              >
                <SimpleTooltip
                  tooltipText={`Revenue Driver #${unit.rank}`}
                  place="bottom"
                >
                  {unit.rank}
                </SimpleTooltip>
              </div>
            )}
            <div
              className={`${
                unit.rank ? "pl-8" : ""
              } flex w-7/12 items-center overflow-x-auto`}
            >
              <div className="mr-1 font-semibold text-gray-800">
                {unit.gam_unit_id}
              </div>
              <div className="truncate mr-1 text-gray-800" title={unit.name}>
                {unit.name}
              </div>
              {unit.deleted_at && (
                <>
                  <span
                    className="rounded mx-1 bg-red-300 px-1 text-xs font-bold uppercase text-red-800"
                    data-tip
                    data-for={`${unit.gam_unit_id}-deleted-at`}
                  >
                    Deleted
                  </span>
                  <ReactTooltip
                    type="dark"
                    effect="solid"
                    place="top"
                    id={`${unit.gam_unit_id}-deleted-at`}
                  >
                    Deleted at:{" "}
                    <DateTimeFormatter
                      datetime={unit.deleted_at}
                    ></DateTimeFormatter>
                  </ReactTooltip>
                </>
              )}
            </div>

            <div className="flex w-5/12 items-center justify-end text-sm leading-none">
              {/* Dummy span to make sure parent flexbox is aligned at baseline */}
              <span className="text-gray-100">.</span>

              <div className="pl-1">
                {unit.parent_units && unit.parent_units.length > 0 && (
                  <UnitHasParentButton
                    unit={unit}
                    networkId={networkId}
                    networkName={networkName}
                  ></UnitHasParentButton>
                )}
              </div>
              <div className="pl-1">
                {unit.child_units && unit.child_units.length > 0 && (
                  <UnitHasChildrenButton
                    unit={unit}
                    networkId={networkId}
                    networkName={networkName}
                  ></UnitHasChildrenButton>
                )}
              </div>

              <div className="pl-1">
                <SegmentResearchSection
                  hasSegmentConfig={unit.has_segment_config}
                ></SegmentResearchSection>
              </div>
              <div>
                <CustomSegSection
                  enabled={autopilot_brief.custom_segmentation}
                ></CustomSegSection>
              </div>
              <div>
                <DebugModeSection
                  enableDispatcherDebug={
                    autopilot_brief.enable_dispatcher_debugging
                  }
                ></DebugModeSection>
              </div>
              <div className="flex items-center gap-1 px-1">
                <UnitStatus unit={unit} showName={isPaused}></UnitStatus>
              </div>
              <div className="px-1">
                <UnitMode unit={unit}></UnitMode>
              </div>
              {/* <div className="pl-1">
                <UnitUpdateMode unit={unit}></UnitUpdateMode>
              </div> */}
            </div>
          </div>
          <div
            className={`-ml-2 -mr-2 -mt-2 px-2 py-2 text-gray-700 ${
              isPaused ? "bg-orange-100" : "bg-gray-100"
            }`}
          >
            <UnitMetadata unit={unit}></UnitMetadata>
          </div>
          <div className="border-b whitespace-no-wrap flex items-center justify-between">
            <div
              className="flex w-full overflow-x-auto"
              style={{ minWidth: "48px" }}
            >
              <RequestTypesSection
                requestTypes={unit.request_types}
              ></RequestTypesSection>
            </div>

            <div className="flex">
              <div>
                <div className="border-l px-1 text-xs text-gray-600">
                  <span className="text-gray-800">YS: </span>
                  <span title="YieldSet ID">{unit.ys_id} </span>
                  <span title="YieldSet Name">{unit.ys_name}</span>
                </div>
              </div>
              <div>
                <AdPodsSection
                  enableAdPods={autopilot_brief.enable_ad_pods}
                ></AdPodsSection>
              </div>
              <div>
                <InspectionSection
                  unitId={unit.gam_unit_id}
                  forceInspection={autopilot_brief.force_inspection}
                ></InspectionSection>
              </div>
              <div>
                <BmSection
                  unitId={unit.gam_unit_id}
                  bpm={autopilot_brief.benchmark_performance_multiplier}
                  enableAbuse={autopilot_brief.enable_benchmark_tfcd_abuse}
                  bmAbuseSizes={autopilot_brief.benchmark_abuse_sizes}
                ></BmSection>
              </div>
              <div>
                <MaxTrafficRatioSection
                  minTrafficRatio={autopilot_brief.minimum_traffic_ratio}
                  maxTrafficRatio={autopilot_brief.maximum_traffic_ratio}
                ></MaxTrafficRatioSection>
              </div>
            </div>
          </div>
          {isBasicInfoOnly ? (
            ""
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="whitespace-no-wrap mb-1 mt-2 flex w-full overflow-x-auto">
                  <DemandTypesSection
                    demandTypes={demandTypes}
                  ></DemandTypesSection>
                </div>
                <div className="mb-1 mt-2 flex">
                  <UnitChangeTimeSection
                    unit={unit}
                    type={type}
                  ></UnitChangeTimeSection>
                </div>
              </div>

              <UnitTagsSection
                tags={unit.tags}
                suspendedRatio={unit.suspended_ratio}
              ></UnitTagsSection>

              <div className="whitespace-no-wrap border-b flex items-center overflow-x-auto py-1">
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle="Request"
                  data={unit.unit_trends}
                  dataKey={"req"}
                  handleMouseEnter={() =>
                    this.setState({ hoverFlag: "Request" })
                  }
                  handleMouseLeave={() => this.setState({ hoverFlag: "" })}
                  roundDecimal={2}
                  isPercentage={false}
                  hideSplotline={true}
                  handleClick={() => {
                    this.handleOpenMetricModal("req_trend");
                  }}
                ></Avg7DaySplotline>
              </div>

              <div className="whitespace-no-wrap border-b flex items-center overflow-x-auto py-1">
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle="Req"
                  title="PScore"
                  description="Intowow optimized (true & false) / Total compatible"
                  data={unit.unit_trends}
                  dataKey="pscore"
                  handleClick={() => this.handleOpenMetricModal("pscore_trend")}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle="Req"
                  title="DScore"
                  description="Intowow optimized (true) / Intowow optimized (true & false)"
                  data={unit.unit_trends}
                  dataKey="dscore"
                  handleClick={() => this.handleOpenMetricModal("dscore_trend")}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle="Req"
                  title="UPR Key"
                  description="Intowow upr key20 / Intowow optimized (true & false)"
                  data={unit.unit_trends}
                  dataKey="uprkey"
                  handleClick={() => this.handleOpenMetricModal("uprkey_trend")}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle="H Rev"
                  title="MScore Algo"
                  description="Intowow algo managed / Intowow upr key20 (optimized)"
                  data={unit.unit_trends}
                  dataKey="algomscore"
                  handleClick={() =>
                    this.handleOpenMetricModal("algomscore_trend")
                  }
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle=""
                  title="IVT Ratio"
                  data={unit.unit_trends}
                  dataKey="ivt_ratio"
                  handleClick={() => this.handleOpenMetricModal("ivt_trend")}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle=""
                  title="Cost Ratio"
                  data={unit.unit_trends}
                  dataKey="cost_ratio"
                  handleClick={() => this.handleOpenMetricModal("cost_trend")}
                ></Avg7DaySplotline>
              </div>

              <div className="whitespace-no-wrap flex items-center overflow-x-auto pt-1">
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  title={netRevTitle.get(NET_REV.GOOGLE)}
                  data={unit.unit_trends}
                  dataKey={"net_google_inc_rev_ratio"}
                  roundDecimal={4}
                  handleMouseEnter={() =>
                    this.setState({ hoverFlag: NET_REV.GOOGLE })
                  }
                  handleMouseLeave={() => this.setState({ hoverFlag: "" })}
                  handleClick={() => {
                    this.handleOpenRevLiftModal(NET_REV.GOOGLE);
                  }}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  title={netRevTitle.get(NET_REV.HOLISTIC)}
                  data={unit.unit_trends}
                  dataKey={"net_holistic_inc_rev_ratio"}
                  handleMouseEnter={() =>
                    this.setState({ hoverFlag: NET_REV.HOLISTIC })
                  }
                  handleMouseLeave={() => this.setState({ hoverFlag: "" })}
                  roundDecimal={4}
                  handleClick={() => {
                    this.handleOpenRevLiftModal(NET_REV.HOLISTIC);
                  }}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  title={netRevTitle.get(NET_REV.BILLABLE)}
                  data={unit.unit_trends}
                  dataKey={"net_billable_lift"}
                  handleMouseEnter={() =>
                    this.setState({ hoverFlag: NET_REV.BILLABLE })
                  }
                  handleMouseLeave={() => this.setState({ hoverFlag: "" })}
                  roundDecimal={4}
                  handleClick={() => {
                    this.handleOpenRevLiftModal(NET_REV.BILLABLE);
                  }}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle=""
                  title="b vs o eCPM"
                  data={unit.unit_trends}
                  dataKey="ecpm_ratio"
                  isPercentage={true}
                  handleClick={() => this.handleOpenMetricModal("ecpm_ratio")}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle=""
                  title="b vs o STR"
                  data={unit.unit_trends}
                  dataKey="str_ratio"
                  isPercentage={true}
                  handleClick={() => this.handleOpenMetricModal("str_ratio")}
                ></Avg7DaySplotline>
                <Avg7DaySplotline
                  unitId={unit.gam_unit_id}
                  subtitle=""
                  title="b vs o IVT"
                  data={unit.unit_trends}
                  dataKey="bo_ivt_ratio"
                  isPercentage={true}
                  handleClick={() =>
                    this.handleOpenMetricModal("ivt_b_o_trend")
                  }
                ></Avg7DaySplotline>
              </div>

              {unitMetricDaysData.map((u, i) => {
                let isMissingCurrentDay = !_.last(unitMetricDaysData);

                return (
                  <React.Fragment key={i}>
                    {u && (
                      <UnitMetricPerDay
                        flag={hoverFlag}
                        unit={u}
                        day={isMissingCurrentDay ? i + 1 : i}
                      ></UnitMetricPerDay>
                    )}
                  </React.Fragment>
                );
              })}
            </>
          )}
          <div className="border-t mt-4 flex justify-between pt-2">
            <DemandTypePortfolioButton
              isInTwd={isInTwd}
              networkId={networkId}
              gamUnitId={unit.ext_gam_unit_id}
              buttonClass="border rounded hover:shadow flex items-center border-blue-300 px-1 text-xs text-blue-500"
            ></DemandTypePortfolioButton>

            <button
              type="button"
              className="border rounded hover:shadow flex items-center border-blue-300 px-1 text-xs text-blue-500"
              onClick={() =>
                this.setState({ selectedModal: MODAL.APPLIED_UPR })
              }
            >
              Appl. UPR
            </button>

            <button
              type="button"
              className="border rounded hover:shadow flex items-center border-blue-300 px-1 text-xs text-blue-500"
              onClick={() => this.setState({ selectedModal: MODAL.APPLIED_FL })}
            >
              Appl. Firstlook
            </button>

            <button
              type="button"
              className="border rounded hover:shadow flex items-center border-blue-300 px-1 text-xs text-blue-500"
              onClick={() => handleGoToCstAnatomy(unit.gam_unit_id)}
            >
              {isPaused && (
                <span className="mr-1">
                  <IoIosPause></IoIosPause>
                </span>
              )}
              CST Anatomy
              <span className="ml-1">
                <FiExternalLink></FiExternalLink>
              </span>
            </button>

            <AutoPilotConfigEditButton
              buttonText={"Auto Pilot Cfg."}
              goToConfigControlPage={() =>
                handleGoToUnitAPConfigPage(unit.gam_unit_id)
              }
            ></AutoPilotConfigEditButton>

            <PopupWrapper
              ref={this.popupRef}
              place="top left"
              triggerType="click"
              hideArrow={false}
              triggerElement={
                <button
                  type="button"
                  className="border rounded hover:shadow flex items-center gap-1 border-blue-300 px-1 text-xs text-blue-500"
                >
                  More
                  <FaCaretDown />
                </button>
              }
              popupElement={
                <div
                  className="border rounded flex flex-col gap-1 border-blue-300 text-xs text-blue-500"
                  onClick={() => {
                    if (this.popupRef.current) {
                      this.popupRef.current.handleClosePopup();
                    }
                  }}
                >
                  <button
                    type="button"
                    className="flex items-center px-2 py-1 hover:bg-gray-400"
                    onClick={() => handleGoToObserver(unit.gam_unit_id)}
                  >
                    Anatomy
                    <span className="ml-1">
                      <FiExternalLink></FiExternalLink>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="flex items-center px-2 py-1 hover:bg-gray-400"
                    onClick={() => handleGoToUnitInsight(unit.gam_unit_id)}
                  >
                    Insight
                    <span className="ml-1">
                      <FiExternalLink></FiExternalLink>
                    </span>
                  </button>
                </div>
              }
            ></PopupWrapper>
          </div>
        </div>

        {selectedModal === MODAL.MONTH_DATA && (
          <UnitRevenueLiftTrendModal
            networkName={networkName}
            networkId={networkId}
            extUnitId={unit.ext_gam_unit_id}
            unitName={unit.name}
            unitId={unit.gam_unit_id}
            dataType={this.state.dataType}
            isInTwd={isInTwd}
            handleClose={() => {
              if (this.props.handleCloseModal) {
                this.props.handleCloseModal();
              }
              this.setState({ selectedModal: null });
            }}
          ></UnitRevenueLiftTrendModal>
        )}

        {selectedModal === MODAL.MONTH_METRIC && (
          <UnitMetricTrendModal
            networkName={networkName}
            networkId={networkId}
            extUnitId={unit.ext_gam_unit_id}
            unitName={unit.name}
            unitId={unit.gam_unit_id}
            metricType={this.state.metricType}
            isInTwd={isInTwd}
            handleClose={() => {
              if (this.props.handleCloseModal) {
                this.props.handleCloseModal();
              }
              this.setState({ selectedModal: null });
            }}
          ></UnitMetricTrendModal>
        )}

        {selectedModal === MODAL.APPLIED_UPR && (
          <AppliedUprModal
            unitId={unit.gam_unit_id}
            unitName={unit.name}
            networkId={networkId}
            startDate={moment()
              .subtract(APPLIED_RULES_DATE_RANGE_DAYS, "days")
              .format("YYYY-MM-DD")}
            includeToday={true}
            handleClose={() => this.setState({ selectedModal: null })}
          />
        )}

        {selectedModal === MODAL.APPLIED_FL && (
          <PublisherFirstLookModal
            unitId={unit.gam_unit_id}
            unitName={unit.name}
            networkId={networkId}
            startDate={moment()
              .subtract(APPLIED_RULES_DATE_RANGE_DAYS, "days")
              .format("YYYY-MM-DD")}
            endDate={moment().format("YYYY-MM-DD")}
            includeToday={true}
            onClose={() => this.setState({ selectedModal: null })}
          />
        )}
      </div>
    );
  }
}

class UnitCardNotOnboarded extends React.PureComponent {
  render() {
    const { unit, demandTypes, showFullWidthCard } = this.props;

    return (
      <>
        <div
          key={unit.ext_gam_unit_id}
          className="border border-gray-300 bg-white p-2 text-sm text-gray-800"
          style={{ width: showFullWidthCard ? "100%" : "20rem" }}
        >
          <div className="-ml-2 -mr-2 -mt-2 bg-gray-100 px-2 py-1 text-gray-700">
            <div className="mr-1 text-gray-600">
              Ext Gam Unit ID: {unit.ext_gam_unit_id}
            </div>
          </div>

          <div className="border-b whitespace-no-wrap flex items-center justify-between">
            <div className="flex w-full overflow-x-auto">
              <RequestTypesSection
                requestTypes={unit.request_types}
              ></RequestTypesSection>
            </div>
          </div>

          <div className="border-b flex w-full justify-between overflow-x-auto">
            {demandTypes && demandTypes.length > 0 ? (
              <div className="whitespace-no-wrap my-2 flex">
                <DemandTypesSection
                  demandTypes={demandTypes}
                ></DemandTypesSection>
              </div>
            ) : (
              <div className="mb-1 h-8"></div>
            )}
          </div>

          <div className="mt-2 flex justify-between text-right">
            <div>
              <div className="text-xs leading-snug">Raw Rev Ratio</div>
              <div className="font-bold">
                <NumberFormat
                  value={unit.raw_eligible_rev_ratio * 100}
                  postfix="%"
                ></NumberFormat>
              </div>
            </div>

            <div>
              <div className="text-xs leading-snug">Client Req</div>
              <div>
                <NumberFormat
                  value={unit.client_request}
                  format="0,0.00a"
                ></NumberFormat>
              </div>
            </div>

            <div>
              <div className="flex flex-col justify-between text-right text-sm">
                <div className="text-xs leading-snug">Raw Eligible Rev</div>
                <div
                  className={`font-semibold ${
                    unit.raw_eligible_rev_trans < 0 ? "text-red-600" : ""
                  }`}
                >
                  <NumberFormat
                    value={unit.raw_eligible_rev_trans}
                    prefix="$"
                  ></NumberFormat>
                </div>
              </div>

              <div className="mt-2 flex flex-col justify-between text-right text-sm">
                <div className="text-xs leading-snug">Raw Eligible Imp</div>

                <div
                  className={unit.raw_eligible_imp < 0 ? "text-red-600" : ""}
                >
                  <NumberFormat
                    value={unit.raw_eligible_imp}
                    format="0,0"
                  ></NumberFormat>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

class UnitMetadata extends React.Component {
  shouldComponentUpdate() {
    return false;
  }

  render() {
    const { unit } = this.props;

    return (
      <>
        <div className="mt-1 flex justify-between text-xs text-gray-600">
          <div className="flex w-full items-center font-mono">
            {unit.ext_gam_unit_id}
            <ClickToCopyWrapper
              copyText={unit.ext_gam_unit_id}
            ></ClickToCopyWrapper>

            {unit.explicitly_targeted > 0 && (
              <SimpleTooltip tooltipText="Explicitly Targeted" place="bottom">
                <span className="rounded ml-2 bg-blue-200 px-1 text-xs font-semibold text-blue-800">
                  EXPL
                </span>
              </SimpleTooltip>
            )}

            {unit.has_explicitly_targeted_ancestor > 0 && (
              <SimpleTooltip
                tooltipText="Has Explicitly Targeted Ancestor"
                place="bottom"
              >
                <span className="rounded ml-2 bg-blue-200 px-1 text-xs font-semibold text-blue-800">
                  ANC EXPL
                </span>
              </SimpleTooltip>
            )}
          </div>

          <div
            className="whitespace-no-wrap w-full overflow-x-auto text-right"
            title="Ext Unit Path"
          >
            {unit.ext_unit_path}
          </div>
        </div>
      </>
    );
  }
}

export default AdUnitBasicCard;
